/**
 * Elements namespace
 * 
 * Provides a declarative API for creating BlockKit elements
 * such as headings, text, list items, etc.
 */

import { HeadingLevel } from './types';

// Base interfaces for element properties
export interface HeadingProps {
  level?: HeadingLevel;
  text: string;
}

export interface TextProps {
  text: string;
  size?: 'small' | 'medium' | 'large';
}

export interface ListItemProps {
  text: string;
  subItems?: ListItemProps[];
}

export interface AlertProps {
  text: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
}

// Base element builder class
abstract class ElementBuilder<T, P> {
  protected props: P;

  constructor(props?: Partial<P>) {
    this.props = props ? { ...props } as P : {} as P;
  }

  // Build method to convert to JSON
  abstract build(): T;

  // Clone method for immutability
  protected clone<B extends ElementBuilder<T, P>>(this: B): B {
    const Constructor = this.constructor as new (props: P) => B;
    return new Constructor({ ...this.props });
  }
}

// Heading element
class HeadingElementBuilder extends ElementBuilder<Record<string, any>, HeadingProps> {
  constructor(props?: Partial<HeadingProps>) {
    super(props);
    this.props = {
      level: 2,
      text: '',
      ...this.props
    };
  }

  level(level: HeadingLevel): HeadingElementBuilder {
    const clone = this.clone();
    clone.props.level = level;
    return clone;
  }

  text(text: string): HeadingElementBuilder {
    const clone = this.clone();
    clone.props.text = text;
    return clone;
  }

  build(): Record<string, any> {
    if (!this.props.text) {
      throw new Error('Heading must have text');
    }

    return {
      type: 'heading',
      level: this.props.level,
      text: this.props.text
    };
  }
}

// Text element
class TextElementBuilder extends ElementBuilder<Record<string, any>, TextProps> {
  constructor(props?: Partial<TextProps>) {
    super(props);
    this.props = {
      text: '',
      size: 'medium',
      ...this.props
    };
  }

  text(text: string): TextElementBuilder {
    const clone = this.clone();
    clone.props.text = text;
    return clone;
  }

  size(size: 'small' | 'medium' | 'large'): TextElementBuilder {
    const clone = this.clone();
    clone.props.size = size;
    return clone;
  }

  build(): Record<string, any> {
    if (!this.props.text) {
      throw new Error('Text must have content');
    }

    return {
      type: 'text',
      text: this.props.text,
      size: this.props.size
    };
  }
}

// Alert element
class AlertElementBuilder extends ElementBuilder<Record<string, any>, AlertProps> {
  constructor(props?: Partial<AlertProps>) {
    super(props);
    this.props = {
      text: '',
      severity: 'info',
      ...this.props
    };
  }

  text(text: string): AlertElementBuilder {
    const clone = this.clone();
    clone.props.text = text;
    return clone;
  }

  severity(severity: 'info' | 'success' | 'warning' | 'error'): AlertElementBuilder {
    const clone = this.clone();
    clone.props.severity = severity;
    return clone;
  }

  build(): Record<string, any> {
    if (!this.props.text) {
      throw new Error('Alert must have text');
    }

    return {
      type: 'alert',
      text: this.props.text,
      severity: this.props.severity
    };
  }
}

// List item element
class ListItemElementBuilder extends ElementBuilder<Record<string, any>, ListItemProps> {
  constructor(props?: Partial<ListItemProps>) {
    super(props);
    this.props = {
      text: '',
      subItems: [],
      ...this.props
    };
  }

  text(text: string): ListItemElementBuilder {
    const clone = this.clone();
    clone.props.text = text;
    return clone;
  }

  addSubItem(subItem: ListItemProps | ListItemElementBuilder): ListItemElementBuilder {
    const clone = this.clone();
    const item = subItem instanceof ListItemElementBuilder ? subItem.build() : subItem;
    clone.props.subItems = [...(clone.props.subItems || []), item];
    return clone;
  }

  build(): Record<string, any> {
    if (!this.props.text) {
      throw new Error('List item must have text');
    }

    return {
      type: 'list_item',
      text: this.props.text,
      subItems: this.props.subItems?.map(item => 
        item instanceof ListItemElementBuilder ? item.build() : item
      )
    };
  }
}

// Import the new element builders
import { StepElementBuilder } from './builders/StepElementBuilder';
import { StepListElementBuilder } from './builders/StepListElementBuilder';
import { CalloutElementBuilder } from './builders/CalloutElementBuilder';

// Elements namespace factory functions
export const Elements = {
  Heading(props?: Partial<HeadingProps>): HeadingElementBuilder { return new HeadingElementBuilder(props); },
  Text(props?: Partial<TextProps>): TextElementBuilder { return new TextElementBuilder(props); },
  Alert(props?: Partial<AlertProps>): AlertElementBuilder { return new AlertElementBuilder(props); },
  ListItem(props?: Partial<ListItemProps>): ListItemElementBuilder { return new ListItemElementBuilder(props); },
  Step(): StepElementBuilder { return new StepElementBuilder(); },
  StepList(): StepListElementBuilder { return new StepListElementBuilder(); },
  Callout(): CalloutElementBuilder { return new CalloutElementBuilder(); }
};

export default Elements;
